import { Box, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { goalRecommendationSchema } from '@/modules/advisors/users/pages/components/GoalRecommendationModal/GoalRecommendationModal.schema'
import type {
  GoalRecommendationModalProps,
  GoalRecommendationSchema,
} from '@/modules/advisors/users/pages/components/GoalRecommendationModal/GoalRecommendationModal.types'

import { Button } from '@/components/Button/Button'
import { Modal } from '@/components/Modal/Modal'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import { useAdvisorsServiceAdvisorGoalsControllerCreateGoalRecommendation } from '@/__generated__/queries'

import classes from './GoalRecommendationModal.module.css'

export function GoalRecommendationModal({
  onClose,
  opened,
  user,
}: GoalRecommendationModalProps) {
  const isMutating = useIsMutating({
    exact: true,
  })

  const goalRecommendationMutation =
    useAdvisorsServiceAdvisorGoalsControllerCreateGoalRecommendation({
      onSuccess: () => {
        notifications.show({
          color: 'green',
          message: `Goal successfully added to ${user?.firstName} ${user?.lastName}'s action plan`,
          title: 'Coaching goal added',
        })

        onClose()
      },
    })

  const form = useForm<GoalRecommendationSchema>({
    validate: zodResolver(goalRecommendationSchema),
  })

  const onSubmit = (values: GoalRecommendationSchema) => {
    if (!user) return

    goalRecommendationMutation.mutate({
      requestBody: { ...values, amount: values.amount ?? 0 },
      userId: user.id,
    })
  }

  const handleDueDateUpdate = (value: Date | undefined) => {
    form.setFieldValue('dueDate', value ? value.toISOString() : '')
  }

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  if (!user) return null

  return (
    <Modal
      classNames={classes}
      onClose={onClose}
      opened={opened}
      withCloseButton={false}
    >
      <Flex align="center" direction="column">
        <Title mb={8} variant="h1">
          Goal
        </Title>

        <Text ta="center">
          Use this to add a financial goal that{' '}
          <b>
            {user.firstName} {user.lastName}
          </b>{' '}
          has expressed interest in working toward. This is an educational
          accountability tool — not a personalized financial recommendation or
          investment advice.
        </Text>
      </Flex>

      <Box component="form" mt={32} noValidate onSubmit={handleSubmit}>
        <TextInput
          {...form.getInputProps('name')}
          label="Goal Title"
          placeholder="Goal"
        />

        <SeparateDateInput
          disablePastDates
          label="Target Date"
          onChange={value =>
            handleDueDateUpdate(value instanceof Date ? value : undefined)
          }
          value={
            form.values.dueDate
              ? dayjs(form.values.dueDate).toDate()
              : undefined
          }
        />

        <NumberInput
          {...form.getInputProps('amount')}
          hideControls
          label="Target Amount (optional)"
          placeholder="Amount"
        />

        <Text c="dimmed" fz="xs" mt={16}>
          Goals logged here reflect the employee&apos;s own stated priorities
          from your coaching session. Do not enter goals based solely on your
          own assessment of what the employee should do. Rolan Wealth is not a
          registered investment adviser and does not provide investment advice.
        </Text>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={32}
        >
          <Button miw="48%" onClick={onClose} variant="outline">
            Cancel
          </Button>

          <Button loading={!!isMutating} miw="48%" type="submit">
            Add Goal
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
}
